import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/sharp-solid-svg-icons';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { SCView } from '../view';
import type { SCCodeEditorProps } from './editor';
import { SCCodeEditor } from './editor';
import type { SCCodeFragmentProps } from './fragment';
import { SCCodeFragment } from './fragment';
import './styles.css';

interface SCCodeEditorAndFragmentProps extends SCCodeEditorProps, SCCodeFragmentProps {
  fragmentTitle?: string;
  editorTitle?: ReactNode;
  maxHeight?: string | number; // Maximum height for the editor
  actions?: { label: string; onPress: () => void; disabled?: boolean; loading?: boolean }[];
}

function SCCodeEditorAndFragment({
  language,
  value,
  onChange,
  disabled,
  autoCompleteOptions,
  items,
  sort,
  fragmentTitle,
  editorTitle,
  actions,
  maxHeight,
  dataTestId,
}: SCCodeEditorAndFragmentProps) {
  // The editor column is height-bounded to this; bound the fragment column to the same so its own
  // scroller engages instead of growing the container (which makes the whole modal body scroll, coupling
  // the two panes).
  const resolvedHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : (maxHeight ?? '400px');

  const customAttributes = useMemo(() => {
    return items.map((item) => item.children.map((child) => child.value)).flat();
  }, [items]);
  return (
    <SCView flex flexCol hFull style={{ minHeight: 0 }}>
      {actions?.length && (
        <SCView justifyEnd flex gap={6} mb={2}>
          {actions.map((action) => (
            <div
              style={{
                cursor: action.disabled || action.loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
              key={action.label}
              onClick={() => {
                if (!(action.disabled || action.loading)) {
                  action.onPress();
                }
              }}
            >
              <span
                className={`sce-action-label ${action.disabled || action.loading ? 'disabled' : ''}`}
              >
                {action.label}
              </span>
              {action.loading && <FontAwesomeIcon spin icon={faSpinnerThird} />}
            </div>
          ))}
        </SCView>
      )}
      <div className="sc-editor-fragment-container">
        {!!items.length && (
          <div
            //className="sc-code-editor-fragment-panel"
            style={{
              minHeight: 0,
              maxHeight: resolvedHeight,
              display: 'flex',
              overflow: 'hidden',
              width: '40%',
              maxWidth: 300,
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {fragmentTitle && (
              <SCView textSm h={5}>
                {fragmentTitle}
              </SCView>
            )}
            <div
              style={{
                // Fill the (stretched) column so the fragment list box matches the editor
                // column's height. Pinning it to `maxHeight` left it ~20px short of the editor,
                // which is taller by its 10px top/bottom padding — that gap was the misalignment.
                // flex:1 inside this definite-height column still gives a definite height, so the
                // nested SCCodeFragment's `height: 100%` resolves and its own scroll engages.
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
              }}
            >
              <SCCodeFragment
                disabled={disabled}
                items={items}
                language={language}
                sort={sort}
                dataTestId={dataTestId}
              />
            </div>
          </div>
        )}
        <SCView
          flex1
          flex
          flexCol
          gap={2}
          style={{
            minWidth: 0,
            minHeight: 0,
            padding: 10,
            backgroundColor: 'var(--surface-base-light)',
          }}
        >
          {editorTitle && <SCView>{editorTitle}</SCView>}
          <SCView
            className="sc-code-editor-scroll-wrapper"
            flex1
            flex
            overflowYAuto
            style={{
              minHeight: maxHeight ?? '400px',
              maxHeight: maxHeight ?? '400px',
            }}
          >
            <SCCodeEditor
              disabled={disabled}
              language={language}
              autoCompleteOptions={autoCompleteOptions}
              value={value}
              onChange={onChange}
              customAttributes={customAttributes}
              maxHeight={maxHeight}
            />
          </SCView>
        </SCView>
      </div>
    </SCView>
  );
}

export { SCCodeEditorAndFragment };
export type { SCCodeEditorAndFragmentProps };
